import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    Directive,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';

import {
    BulkSplitViewDetails,
    BulkSplitViewEntries,
    BulkSplitViewFilter,
    SplitViewDetails,
    SplitViewEntries,
    SplitViewFilter
} from '@webmate-web-core/shared/components/split-view/interfaces/split-view.interface';
import {BehaviorSubject, isObservable, Observable, Subject} from 'rxjs';
import {CommonModule} from '@angular/common';

@Directive({
    standalone: true,
    selector: '[wmSplitView]',
})
export class SplitViewDirective {
    @Input('wmSplitView') name: 'entries' | 'details' | 'filter' | 'bulkdetails';
}

@Component({
    selector: 'wm-split-view',
    templateUrl: 'split-view.component.html',
    styleUrls: ['split-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule]
})
export class SplitViewComponent<T> implements AfterContentInit, OnChanges {

    // TODO build option to allow pagination as a data source.

    // TODO: implement pagination (build dedicated pagination filter component that passes its parameters to the pagination
    //  instead of applying it directly)

    @Input() entities: T[] | Observable<T[]>;

    @Input() allowBulkMode?: boolean = false;

    @Input() emptyState: string = 'No data provided';

    @Input() leftColumnWidth: string = '350px';

    @Input() noEntitiesFromFilter: boolean = false;

    dataEmpty: boolean = true;
    filterEmpty: boolean = false;

    @ContentChild(SplitViewFilter) filter: SplitViewFilter<T>;
    @ContentChild(SplitViewEntries) entries: SplitViewEntries<T>;
    @ContentChild(SplitViewDetails) details: SplitViewDetails<T>;
    @ContentChild(BulkSplitViewDetails) bulkDetails: BulkSplitViewDetails<T>;

    private inputRef: SplitViewFilter<T> | SplitViewEntries<T>;

    private inputForwardingSubject?: Subject<T[]>;

    private dataSource$: Observable<T[]>;

    bulkModeActive = false;

    constructor(private cdRef: ChangeDetectorRef) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.inputForwardingSubject && changes['entities']) {
            // Update changes to the entities, if they don't come from an Observable
            this.inputForwardingSubject.next(changes['entities'].currentValue);
        }
    }


    ngAfterContentInit(): void {

        // Set up bulk mode
        if (this.allowBulkMode) {
            if (!this.bulkDetails) {
                throw new Error('Must provide a BulkSplitViewDetails component if setting allowBulkMode to true');
            }

            if (!(this.entries instanceof BulkSplitViewEntries)) {
                throw new Error('Must use subclass of BulkSplitViewEntries if setting allowBulkMode to true');
            } else {
                this.entries.bulkDetails = this.bulkDetails;
            }

            if (!(this.filter instanceof BulkSplitViewFilter)) {
                throw new Error('Must use subclass of BulkSplitViewFilter if setting allowBulkMode to true');
            } else {
                this.filter.bulkModeConfig = {
                    initialState: false,
                    toggleAllCallback: (b: boolean) => {
                        (this.entries as BulkSplitViewEntries<T>).toggleAll(b);
                    },
                    bulkModeToggleCallback: (b: boolean) => {
                        this.bulkModeActive = b;
                        (this.entries as BulkSplitViewEntries<T>).bulkModeActive = b;
                        this.cdRef.markForCheck();
                    }
                };
            }
        }

        this.entries.detailView = this.details;


        // TODO refine concept of in- and outputs (maybe create of subject for the non-observable input case?)
        this.inputRef = (this.filter && !this.noEntitiesFromFilter) ? this.filter : this.entries;

        if (this.filter && !this.noEntitiesFromFilter) {
            this.filter.entitiesChanged.subscribe(e => {
                this.entries.entities = e;
                this.filterEmpty = e.length === 0;
                this.cdRef.markForCheck();
            });
        }

        // Check if input is an Observable and if not wrap it into a Subject
        if (isObservable(this.entities)) {
            this.dataSource$ = this.entities;
        } else {
            this.inputForwardingSubject = new BehaviorSubject(this.entities);
            this.dataSource$ = this.inputForwardingSubject.asObservable();
        }

        this.dataSource$.subscribe(e => {
            this.inputRef.entities = e;
            this.dataEmpty = e.length === 0;
        });
    }

}
